import React, { useRef, useEffect, useContext } from 'react';
import ReactDOMServer from 'react-dom/server';
import MapContext from './MapContext';
import { IntlProvider, useIntl } from 'react-intl';

const MapInfoWindow = ({ hideOverlay, position, children, onOverlayDrawn, handleClick }) => {
  const overlayRef = useRef(null);
  const elRef = useRef(null);
  const map = useContext(MapContext);
  const intl = useIntl();

  const removeOverlay = () => {
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
    }
  };

  const handleTileClick = (unitSerialNumber) => {
    handleClick(unitSerialNumber);
  };

  useEffect(() => {
    // Cleanup function to remove overlay when component unmounts
    return () => {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
    };
  }, []);

  useEffect(() => {
    // Handle hiding overlay
    if (hideOverlay) {
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }
      return;
    }

    // Only proceed if we have a map
    if (!map) {
      return;
    }

    // Create or update the overlay
    const createOverlay = async () => {
      // Initialize el if needed
      if (!elRef.current) {
        elRef.current = document.createElement('div');
      }

      // Get locale and messages from intl context
      const locale = intl?.locale || 'en';
      const messages = intl?.messages || {};

      // Wrap children in IntlProvider for server-side rendering
      const contentWithIntl = (
        <IntlProvider locale={locale} messages={messages}>
          {children}
        </IntlProvider>
      );

      const content = ReactDOMServer.renderToStaticMarkup(contentWithIntl);

      try {
        const bubbleOverlay = await import('./BubbleOverlay');
        
        // Remove existing overlay if present
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
        }

        // Create new overlay
        overlayRef.current = new bubbleOverlay.default({
          location: position,
          object: children.props.unitserialnumber,
          map: map,
          onClick: () => {
            handleTileClick(children.props.unitserialnumber);
          },
          content: content
        });

        overlayRef.current.setMap(map);

        // Call the onOverlayDrawn callback if provided
        if (onOverlayDrawn) {
          onOverlayDrawn({
            overlay: overlayRef.current,
            removeOverlay
          });
        }
      } catch (error) {
        console.error('Failed to load BubbleOverlay:', error);
      }
    };

    createOverlay();
  }, [map, hideOverlay, position, children, intl, onOverlayDrawn, handleClick]);

  // The component doesn't render anything directly - it only creates map overlays
  return null;
};

export default MapInfoWindow;